import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./AskScoldy.css";

const PREMIUM_GROUPS = [6, 7, 8, 9];

const getStoredUser = () => {
  try {
    const raw = localStorage.getItem("currentUser");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};


const cleanResearchAnswer = (text = "") => String(text)
  .replace(/\(\s*\[[^\]]+\]\(https?:\/\/[^)]+\)\s*\)/gi, "")
  .replace(/\[[^\]]+\]\(https?:\/\/[^)]+\)/gi, "")
  .replace(/\(\s*https?:\/\/[^\s)]+\s*\)/gi, "")
  .replace(/https?:\/\/\S+/gi, "")
  .replace(/[ \t]+\n/g, "\n")
  .replace(/\n{3,}/g, "\n\n")
  .replace(/[ \t]{2,}/g, " ")
  .trim();

const renderFormattedText = (text = "") =>
  String(text).split(/(\*\*[^*]+\*\*)/g).map((part, index) =>
    part.startsWith("**") && part.endsWith("**") && part.length > 4
      ? <strong key={index}>{part.slice(2, -2)}</strong>
      : <React.Fragment key={index}>{part}</React.Fragment>
  );

const AskScoldy = () => {
  const navigate = useNavigate();
  const currentUser = useMemo(() => getStoredUser(), []);
  const isPremium = PREMIUM_GROUPS.includes(Number(currentUser?.group_id));

  const [mode, setMode] = useState("ask");
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(isPremium);
  const [statusError, setStatusError] = useState("");
  const [question, setQuestion] = useState("");
  const [messages, setMessages] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [requestError, setRequestError] = useState("");
  const endRef = useRef(null);

  const loadStatus = async () => {
    if (!isPremium) {
      setStatusLoading(false);
      return;
    }

    try {
      setStatusError("");
      const response = await fetch("/backend/ai/status", {
        method: "GET",
        credentials: "include",
        cache: "no-store",
      });
      const result = await response.json().catch(() => null);
      if (!response.ok || !result?.success) {
        throw new Error(result?.error || "Unable to load your AI allowance.");
      }
      setStatus(result);
    } catch (error) {
      setStatusError(error.message || "Unable to load your AI allowance.");
    } finally {
      setStatusLoading(false);
    }
  };

  useEffect(() => {
    loadStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPremium]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [messages, submitting]);

  const activeAllowance = mode === "research" ? status?.research : status?.ask;
  const planName = status?.group?.name || "";
  const modeLabel = mode === "research" ? "Scoldy AI Research" : "Ask Scoldy AI";

  const submitQuestion = async (event) => {
    event.preventDefault();
    const trimmed = question.trim();

    if (!trimmed || submitting || !isPremium) return;

    if (activeAllowance && Number(activeAllowance.remaining) <= 0) {
      setRequestError(`Your ${modeLabel} allowance has been used for this month.`);
      return;
    }

    // Include recent exchanges so each API call can understand follow-up questions.
    // Keep the context bounded to control token usage and backend request size.
    const priorTurns = messages
      .filter((message) => message.mode === mode &&
        (message.role === "user" || message.role === "assistant"))
      .slice(-16);
    const contextLines = priorTurns.map((message) =>
      `${message.role === "user" ? "User" : "Assistant"}: ${String(message.text || "").trim()}`
    );
    let conversationContext = contextLines.join("\n\n");
    while (conversationContext.length > 10000 && contextLines.length > 0) {
      contextLines.shift();
      conversationContext = contextLines.join("\n\n");
    }

    setMessages((previous) => [...previous, {
      id: `${Date.now()}-user`,
      role: "user",
      mode,
      text: trimmed,
    }]);
    setQuestion("");
    setRequestError("");
    setSubmitting(true);

    try {
      const response = await fetch("/backend/ai/chat", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mode, question: trimmed, context: conversationContext }),
      });
      const result = await response.json().catch(() => null);

      if (!response.ok || !result?.success) {
        throw new Error(result?.error || "Scoldy AI could not complete this request.");
      }

      setMessages((previous) => [...previous, {
        id: `${Date.now()}-assistant`,
        role: "assistant",
        mode: result.mode || mode,
        text: result.answer || "",
        sources: Array.isArray(result.sources) ? result.sources : [],
      }]);

      if (result.entitlement) {
        setStatus({
          success: true,
          eligible: result.entitlement.eligible,
          group: result.entitlement.group,
          period: result.entitlement.period,
          ask: result.entitlement.ask,
          research: result.entitlement.research,
        });
      } else {
        await loadStatus();
      }
    } catch (error) {
      setRequestError(error.message || "Scoldy AI could not complete this request.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleModeChange = (nextMode) => {
    if (submitting || nextMode === mode) return;
    setMode(nextMode);
    setRequestError("");
  };

  if (!isPremium) {
    return (
      <div className="ask-scoldy-container">
        <h1 className="ask-scoldy-header">Ask Scoldy AI</h1>
        <p className="ask-scoldy-subtitle">Ask. Understand. Research.</p>
        <div className="ask-scoldy-gate">
          <div className="ask-scoldy-gate-mark">✦</div>
          <h2>Ask Scoldy AI is a Premium feature</h2>
          <p>Ask Scoldy AI and Scoldy AI Research are included with Silver, Gold, Platinum and Diamond plans.</p>
          <button type="button" onClick={() => navigate("/cloud9")}>View Premium Plans</button>
        </div>
      </div>
    );
  }

  return (
    <div className="ask-scoldy-container">
      <div className="ask-scoldy-top">
        <div>
          <h1 className="ask-scoldy-header">Ask Scoldy AI</h1>
          <p className="ask-scoldy-subtitle">Ask. Understand. Research.</p>
        </div>
        {status && <div className="ask-scoldy-plan-badge">{planName} Plan</div>}
      </div>

      <div className="ask-scoldy-mode-tabs" role="tablist" aria-label="AI mode">
        <button type="button" className={mode === "ask" ? "active" : ""} onClick={() => handleModeChange("ask")} disabled={submitting}>Ask Scoldy AI</button>
        <button type="button" className={mode === "research" ? "active" : ""} onClick={() => handleModeChange("research")} disabled={submitting}>Scoldy AI Research</button>
      </div>

      <div className="ask-scoldy-allowance">
        {statusLoading ? "Loading allowance..." : statusError ? (
          <span className="ask-scoldy-error">{statusError}</span>
        ) : activeAllowance ? (
          <><strong>{planName} Plan</strong><span> · </span><strong>{activeAllowance.remaining}</strong> of <strong>{activeAllowance.limit}</strong> {mode === "research" ? "AI Research requests" : "Ask questions"} remaining</>
        ) : null}
      </div>

      {mode === "research" && (
        <div className="ask-scoldy-research-note">
          Scoldy AI Research uses current external information and identifies its sources. Each submitted Research question uses one Research request after a successful response.
        </div>
      )}

      <div className={`ask-scoldy-conversation ${messages.length === 0 ? "empty" : "has-messages"}`} aria-live="polite">
        {messages.length === 0 && (
          <div className="ask-scoldy-empty">
            <div className="ask-scoldy-empty-mark">✦</div>
            <h2>{mode === "research" ? "What would you like Scoldy to research?" : "What would you like to ask Scoldy?"}</h2>
            <p>{mode === "research" ? "Use Research when you need current external information and identified sources." : "Ask for explanations, ideas, general knowledge or help understanding a topic."}</p>
          </div>
        )}

        {messages.map((message) => (
          <div key={message.id} className={`ask-scoldy-message ${message.role}`}>
            <div className="ask-scoldy-message-label">
              {message.role === "user" ? "You" : message.mode === "research" ? "Scoldy AI Research" : "Ask Scoldy AI"}
            </div>
            <div className="ask-scoldy-message-text">
              {renderFormattedText(message.role === "assistant" && message.mode === "research" ? cleanResearchAnswer(message.text) : message.text)}
            </div>
            {message.role === "assistant" && message.mode === "research" && message.sources?.length > 0 && (
              <div className="ask-scoldy-sources">
                <div className="ask-scoldy-sources-title">Sources</div>
                {message.sources.map((source, index) => (
                  <a key={`${source.url || "source"}-${index}`} href={source.url} target="_blank" rel="noopener noreferrer">
                    {source.title || source.url || `Source ${index + 1}`}
                  </a>
                ))}
              </div>
            )}
          </div>
        ))}

        {submitting && (
          <div className="ask-scoldy-message assistant pending">
            <div className="ask-scoldy-message-label">{modeLabel}</div>
            <div className="ask-scoldy-thinking">{mode === "research" ? "Researching..." : "Thinking..."}</div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {requestError && <div className="ask-scoldy-request-error">{requestError}</div>}

      <form className="ask-scoldy-composer" onSubmit={submitQuestion}>
        <textarea
          value={question}
          onChange={(event) => setQuestion(event.target.value)}
          placeholder={mode === "research" ? "Enter your research question here..." : "Enter your question here..."}
          rows={2}
          disabled={submitting || statusLoading || Boolean(statusError)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
              event.preventDefault();
              event.currentTarget.form?.requestSubmit();
            }
          }}
        />
        <div className="ask-scoldy-composer-footer">
          <span>Enter to send · Shift+Enter for a new line</span>
          <button type="submit" disabled={submitting || statusLoading || Boolean(statusError) || !question.trim() || Number(activeAllowance?.remaining ?? 0) <= 0}>
            {submitting ? (mode === "research" ? "Researching..." : "Sending...") : mode === "research" ? "Research" : "Ask Scoldy"}
          </button>
        </div>
      </form>

      <p className="ask-scoldy-disclaimer">AI can make mistakes. Evaluate important information and check the identified sources when appropriate.</p>
    </div>
  );
};

export default AskScoldy;
